import { mimeTypes } from '@/shared/constants';
import { type DriveFile } from '@/shared/DriveTypes';
import { ErrorOutline, OpenInNew } from '@mui/icons-material';
import { Backdrop, CircularProgress, Modal, Paper, Skeleton } from '@mui/material';
import cx from 'classnames';
import { Suspense, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { match } from 'ts-pattern';
import { useLocation } from 'wouter';
import * as appStyles from '~/app.css';
import { FileModal } from '~/components/FileModal/FileModal';
import * as styles from '~/components/Page/Page.css';
import { StaticTree } from '~/components/Tree/StaticTree';
import { useCreate, useMutateFile } from '~/components/Tree/treeMenuUtils';
import { Breadcrumbs, EmptyBreadCrumbs } from '~/elements/Breadcrumbs/Breadcrumbs';
import * as buttonStyles from '~/elements/Buttons/Button.css';
import EditButton from '~/elements/Buttons/Edit';
import { CheckboxMacro } from '~/elements/CheckboxMacro';
import { CopyPage } from '~/elements/CopyPage/CopyPage';
import { CopyToClipboardMacro } from '~/elements/CopyToClipboardMacro';
import { SheetMacro } from '~/elements/SheetSettings/SheetSettings';
import { SkeletonMacro } from '~/elements/SkeletonMacro';
import Title, { EditableTitle } from '~/elements/Title/Title';
import { TreeMacro } from '~/elements/TreeMacro';
import { colors, darkTheme, lightTheme } from '~/theme.css';
import { DEV, isBun, LEGACY, newFileId, preview } from '~/utils/constants';
import { extractHeadings } from '~/utils/extractHeadings';
import { useAuth, useExport, useFile, useInternalNavigation, useKeyPress, useOnMount } from '~/utils/hooks/hooks';
import { getTitle, isMarkdownLink } from '~/utils/isMarkdownLink';
import { parseExport } from '~/utils/parseExport';
import registerComponent from '~/utils/registerComponent';
import { useStore, useTheme } from '~/utils/store';
import {
  FixedSizeMap,
  getFileTypeName,
  getIndexPage,
  getMimeTypeNameFromFile,
  getNewFileMessage,
  hasAPublicPermission,
  isADocument,
  isAFolder,
  isAForm,
  isAPDF,
  isAPowerpoint,
  isASlide,
  isAVideo,
  isEmpty,
  rem,
} from '~/utils/utils';
import { ActionsDrawer } from '../ActionsDrawer/ActionsDrawer';
import { AddNewContainer } from '../AddMenu/AddNewContainer';
import { LastModified } from '../LastModified/LastModified';
import { Outline } from '../Outline/Outline';
import PrevNext, { EmptyPrevNext } from '../PrevNext/PrevNext';
import { RecentlyChanged } from '../RecentlyChanged/RecentlyChanged';
import { Content } from './Content';
import IndexPage from './IndexPage';
import { InlineEditor } from './InlineEditor';
import { usePageState } from './usePageState';
import { WelcomePage } from './WelcomePage';

type Props = {
  wikiId: string;
  id?: string | undefined;
};

const PROGRESS_KEY = 'page-progress';
const Progress = () => (
  <div className={appStyles.flex}>
    <CircularProgress style={{ color: colors.spinner.default }} thickness={4} size={34} />
  </div>
);

const prefixMap = new FixedSizeMap<string, string>(1);
const NewFileMessage = ({ file }: { file: DriveFile }) => {
  if (!prefixMap.has(file.id)) prefixMap.set(file.id, getNewFileMessage());
  const prefix = prefixMap.get(file.id);
  const type = getFileTypeName(file);
  const title = getTitle(file);
  return (
    <>
      {prefix} the <strong>{title}</strong> {type}
    </>
  );
};

const Page = ({ wikiId, id = wikiId }: Props) => {
  const navigate = useLocation()[1];
  const pageRef = useInternalNavigation();
  const { data: user } = useAuth();
  const newFile = useStore((state) => state.newFile);
  const { data, state } = usePageState({ wikiId, id });
  const { swrFiles, swrFile, swrExport, content, page, children, folderLevelLoaded } = data;
  const { data: allFiles, files, isLoading: filesLoading } = swrFiles;
  const { data: file } = swrFile;
  const { data: wiki, error: wikiError } = useFile(wikiId, [], wikiId);
  const mutateFile = useMutateFile({ file, files: allFiles, wiki });
  const isTablet = useStore((state) => state.isTablet);

  const { url, markdown } = swrExport.data ?? {};
  const isFolder = isAFolder(file);
  const canEdit = Boolean(file?.capabilities?.canEdit);
  const handleRename = (newName: string) => mutateFile({ name: newName });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const handleAddNew = () => {
    setNewFileName('Untitled Document');
    setAddModalOpen(true);
  };
  const create = useCreate({ files: allFiles, wikiId });
  const theme = useTheme();
  const actionsDrawer = (
    <>
      <ActionsDrawer wiki={wiki} file={file} user={user} canEdit={canEdit} onAddNew={handleAddNew} />
      <Modal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        closeAfterTransition
        slots={{
          backdrop: Backdrop,
        }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        className={cx({
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
      >
        <div>
          <FileModal
            title="New Page"
            modalType="document"
            onCancel={() => setAddModalOpen(false)}
            onSubmit={(e) => {
              e.preventDefault();
              const parents = isFolder ? ([file?.id] as [string]) : (file?.parents ?? [wikiId]);
              create({ name: newFileName, mimeType: mimeTypes.document, parents });
              setAddModalOpen(false);
            }}
            defaultValue="Untitled Document"
            onChange={(e) => setNewFileName(e.target.value)}
          />
        </div>
      </Modal>
    </>
  );

  useEffect(() => {
    if (!id || !content.length) return;

    const hash = window.location.hash.slice(1);
    if (!hash) return;

    const element = document.getElementById(hash);
    if (element) return element.scrollIntoView();

    const target = document.getElementById('app');
    if (!target) return;

    const observer = new MutationObserver(() => {
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView();
        observer.disconnect();
      }
    });

    observer.observe(target, { childList: true, subtree: true });

    const timeout = setTimeout(() => observer.disconnect(), 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [id, content.length]);

  const keyPressHandler = DEV
    ? () => {
        const { href, search } = window.location;
        preview
          ? window.open(file?.webViewLink.replace('edit', 'preview'), '_blank')
          : window.open(href + `${search.startsWith('?') ? '&' : '?'}preview`, '_blank');
      }
    : () => {};

  useKeyPress('p', keyPressHandler);

  // if the page id is using new-- prefix, redirect to the wiki
  useOnMount(() => {
    if (isBun) return;
    if (id.startsWith(newFileId)) {
      navigate(`/app/page/${wikiId}/${wikiId}`);
    }
  });

  if (!wiki && wikiError) {
    return (
      <div className={styles.page}>
        <div className={styles.content}>
          <div className={styles.titleContainer}>
            <Title className={styles.title}>Wiki not found</Title>
          </div>
          <p style={{ fontSize: 15 }}>This wiki may have been trashed or you don't have access to it.</p>
          <a
            href={`https://drive.google.com/open?id=${wikiId}`}
            rel="noreferrer"
            target="_blank"
            className={cx(buttonStyles.primary, styles.errorButton)}
          >
            View in Google Drive
            <OpenInNew className={styles.errorButtonIcon} />
          </a>
        </div>
      </div>
    );
  }

  if (!wiki) return null;

  if (state.all.isNew && newFile) {
    return (
      <div ref={pageRef} className={styles.page}>
        {wiki && <Breadcrumbs files={files} file={newFile} wiki={wiki} />}
        <div className={styles.titleContainer}>
          {canEdit ? (
            <EditableTitle className={styles.title} onRename={handleRename}>
              {getTitle(newFile)}
            </EditableTitle>
          ) : (
            <Title className={styles.title}>{getTitle(newFile)}</Title>
          )}
        </div>
        <div className={styles.loading}>
          <Progress key={PROGRESS_KEY} />
        </div>
        <div
          style={{
            marginTop: '2.7rem',
            marginBottom: '1rem',
          }}
        >
          <div className={styles.prevNextContainer}>
            {wiki && <PrevNext file={newFile} files={files} wiki={wiki} />}
          </div>
        </div>
        {actionsDrawer}
      </div>
    );
  }

  if (state.all.isError) {
    const listFile = files.find((f) => f.id === id);
    return (
      <div className={styles.page}>
        <div className={styles.content}>
          <Breadcrumbs
            files={files}
            file={
              {
                id,
                parents: [wiki.id],
                name: 'File not found',
              } as DriveFile
            }
            wiki={wiki}
          />
          <div className={styles.titleContainer}>
            <Title className={styles.title}>File not found</Title>
          </div>
          <p style={{ fontSize: 15 }}>This file may have been trashed or you don't have access to it.</p>
          {listFile ? (
            <a
              href={listFile.webViewLink}
              rel="noreferrer"
              target="_blank"
              className={cx(buttonStyles.primary, styles.errorButton)}
            >
              View file
              <OpenInNew className={styles.errorButtonIcon} />
            </a>
          ) : (
            <a
              href={`https://drive.google.com/open?id=${id}`}
              rel="noreferrer"
              target="_blank"
              className={cx(buttonStyles.primary, styles.errorButton)}
            >
              View file
              <OpenInNew className={styles.errorButtonIcon} />
            </a>
          )}
        </div>
        <div className={styles.prevNextContainer}>
          {folderLevelLoaded ? <PrevNext file={listFile ?? wiki} files={files} wiki={wiki} /> : <EmptyPrevNext />}
        </div>
        {actionsDrawer}
      </div>
    );
  }

  if (state.all.isLoading) {
    return (
      <div className={styles.page}>
        <EmptyBreadCrumbs />
        <div className={styles.loading}>
          <Progress key={PROGRESS_KEY} />
        </div>
        {actionsDrawer}
      </div>
    );
  }

  if (state.wiki.isEmpty) {
    return (
      <div ref={pageRef} className={styles.page}>
        <EmptyBreadCrumbs />
        <div style={{ marginTop: rem(13.5) }}>
          <WelcomePage wiki={wiki} files={files} />
        </div>
        {actionsDrawer}
      </div>
    );
  }

  if (isFolder) {
    return (
      <div ref={pageRef} className={styles.page}>
        {isTablet && wiki.id === id ? null : <Breadcrumbs files={files} file={file} wiki={wiki} />}
        <div className={styles.titleContainer}>
          {state.folder.status !== 'loading' && canEdit ? (
            <EditableTitle className={styles.title} onRename={handleRename}>
              {getTitle(file)}
            </EditableTitle>
          ) : (
            <Title className={styles.title}>{getTitle(file)}</Title>
          )}
        </div>
        {id === wikiId && (filesLoading || files.length > 0) && (
          <RecentlyChanged files={files} wiki={wiki} isLoading={filesLoading} />
        )}
        {match(state.folder.status)
          .with('loading', () => (
            <div className={styles.loading}>
              <Progress key={PROGRESS_KEY} />
            </div>
          ))
          .with('new', () => (
            <div className={styles.content}>
              <NewFileMessage file={file} />
              <br />
              <br />
              <ul style={{ paddingLeft: 40, margin: 0, marginBottom: '.5rem' }}>
                <li>
                  To add to this folder click&nbsp;&nbsp;{' '}
                  <AddNewContainer wiki={wiki} file={file} files={files} animation="left" />
                </li>
              </ul>
            </div>
          ))
          .with('empty', () => (
            <div className={styles.content}>
              <AddNewContainer wiki={wiki} file={file} files={files} animation="left" />
            </div>
          ))
          .with('emptyPage', () => (
            <div className={styles.content}>
              <EditButton file={page} wiki={wiki} animation="left" />
            </div>
          ))
          .with('hasChildren', () => (
            <div className={styles.content} style={{ paddingBottom: '.1rem' }}>
              <StaticTree wiki={wiki} files={children} />
            </div>
          ))
          .with('hasTreeMacro', () =>
            content ? (
              <>
                <PageContent
                  key={file.id}
                  content={content}
                  file={file}
                  files={allFiles}
                  wiki={wiki}
                  url={url}
                  pageRef={pageRef}
                />
                <Suspense>
                  <TreeMacro wiki={wiki} files={children} content={content} query={`[data-react="Tree"]`} />
                </Suspense>
              </>
            ) : null
          )
          .otherwise(() =>
            content ? (
              <PageContent
                key={file.id}
                content={content}
                file={file}
                files={allFiles}
                wiki={wiki}
                url={url}
                pageRef={pageRef}
              />
            ) : null
          )}
        {wiki && (
          <div className={styles.prevNextContainer}>
            <PrevNext file={file} files={files} wiki={wiki} />
          </div>
        )}
        {actionsDrawer}
      </div>
    );
  }

  if (!file) return null;

  const headings = extractHeadings(content);
  const showOutline = isADocument(file) && headings.length > 0;

  return (
    <div className={cx(styles.container, { [styles.containerEditing]: state.page.editing })}>
      <div ref={pageRef} className={cx(styles.page, { [styles.pageEditing]: state.page.editing })}>
        {!state.page.editing &&
          (wiki ? (
            <Breadcrumbs files={files} file={file} wiki={wiki} markdown={!showOutline ? markdown : undefined} />
          ) : (
            <EmptyBreadCrumbs />
          ))}
        <div className={cx(styles.titleContainer, { [styles.titleContainerEditing]: state.page.editing })}>
          {canEdit ? (
            <EditableTitle
              className={cx(styles.title, {
                [styles.editingTitle]: state.page.editing,
              })}
              onRename={handleRename}
            >
              {getTitle(file)}
            </EditableTitle>
          ) : (
            <Title
              className={cx(styles.title, {
                [styles.editingTitle]: state.page.editing,
              })}
            >
              {getTitle(file)}
            </Title>
          )}
        </div>

        {state.page.showLastModified && !state.page.editing && <LastModified file={file} content={content} />}
        {match(state.page.status)
          .with('loading', () => (
            <div className={styles.loading}>
              <Progress key={PROGRESS_KEY} />
            </div>
          ))
          .with('new', () => (
            <div className={styles.content}>
              <NewFileMessage file={file} />
              <br />
              <br />
              <ul style={{ paddingLeft: 40, margin: 0, marginBottom: '.5rem' }}>
                <li>
                  To {isMarkdownLink(file) ? 'view' : 'edit'} this {isMarkdownLink(file) ? 'link' : 'page'}{' '}
                  click&nbsp;&nbsp; <EditButton file={file} wiki={wiki} animation="left" />
                </li>
              </ul>
            </div>
          ))
          .with('empty', () => (
            <div className={styles.content}>
              <EditButton file={file} wiki={wiki} animation="left" />
            </div>
          ))
          .with('error', () => (
            <div className={styles.content}>
              <h2>You need access</h2>
              <p style={{ fontSize: 15 }}>Request access, or switch to an account with access.</p>
              <a
                href={`https://drive.google.com/open?id=${file.id}`}
                rel="noreferrer"
                data-test-id="button-request-access"
                target="_blank"
                className={buttonStyles.primary}
                style={{
                  display: 'inline-flex',
                  textDecoration: 'none',
                  position: 'relative',
                  paddingRight: '2.6rem',
                }}
              >
                Request access
                <OpenInNew style={{ position: 'absolute', right: '.8rem', fontSize: 20 }} />
              </a>
            </div>
          ))
          .with('editing', () => <InlineEditor file={file} />)
          .otherwise(() => (
            <PageContent
              key={file.id}
              content={content}
              file={file}
              files={allFiles}
              wiki={wiki}
              url={url}
              pageRef={pageRef}
            />
          ))}
        {!state.page.editing && (
          <div className={styles.prevNextContainer}>
            {folderLevelLoaded ? (
              <PrevNext file={file} files={files} wiki={wiki} />
            ) : (
              <EmptyPrevNext style={{ visibility: content.length === 0 ? 'hidden' : undefined }} />
            )}
          </div>
        )}
        {actionsDrawer}
      </div>
      {showOutline && (
        <div className={styles.outlineContainer}>
          <EmptyBreadCrumbs style={{ width: 0, margin: 0 }} />
          <div style={{ marginTop: '.85rem', paddingTop: 12 }}>
            {markdown && (
              <CopyPage
                markdown={markdown}
                className={styles.copyPageOutline}
                fileId={file.id}
                isPublic={hasAPublicPermission(wiki)}
              />
            )}
            <Outline headings={headings} />
          </div>
        </div>
      )}
    </div>
  );
};

/** This is only used by Playwright */
export const PageSpec = ({ html }: { html: string }) => {
  const { content } = html
    ? parseExport({ html, file: undefined, files: [], wiki: undefined, codeLineNumbers: false })
    : { content: '' };
  return (
    <main
      className={cx(styles.page, 'document')}
      style={{
        marginTop: '2.8rem',
        maxWidth: 'none',
      }}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

export default Page;

export const PageContent = ({
  content,
  file,
  files,
  wiki,
  url,
  pageRef,
}: {
  content: string | undefined;
  file: DriveFile;
  files: DriveFile[];
  wiki: DriveFile | undefined;
  url: string | undefined;
  height?: number | undefined;
  pageRef: React.RefObject<HTMLElement | null>;
}) => {
  const fileType = getMimeTypeNameFromFile(file);
  const indexPage = getIndexPage(file, files);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const timerRef = useRef<Timer>(undefined);

  if (isASlide(file) || isAPowerpoint(file)) {
    const timeout = 600;
    const delay = (timeout + 100) / 100;
    return (
      <div className={cx(styles.content, fileType)}>
        {showSkeleton && (
          <Skeleton
            className={appStyles.fadeIn}
            style={{ position: 'absolute', left: 0, top: 0, aspectRatio: '16 / 9', height: 'auto' }}
            variant="rectangular"
            width="100%"
            height={'calc(100% - 4px)'}
          />
        )}
        <iframe
          src={file.webViewLink.replace(/\/(edit|view)\?usp=drivesdk.*/, '/preview')}
          title={file.name}
          width="100%"
          height="100%"
          style={{
            border: 'none',
            aspectRatio: '16 / 9',
            width: 'calc(100% + 4rem)',
            height: 'auto',
            padding: '0 2rem',
            margin: '0 -2rem',
            background: 'transparent',
            opacity: 0,
            transition: `opacity .3s .${delay}s`,
          }}
          onLoad={(e) => {
            const { target } = e;
            if (target instanceof HTMLElement) {
              target.style.opacity = '1';
            }
            timerRef.current = setTimeout(() => setShowSkeleton(false), timeout);
          }}
        />
      </div>
    );
  }

  if (isAForm(file)) {
    const timeout = 600;
    const delay = (timeout + 100) / 100;
    return (
      <div className={cx(styles.content, fileType)}>
        {showSkeleton && (
          <Skeleton
            className={appStyles.fadeIn}
            style={{
              margin: 'auto',
              marginTop: 12,
              borderRadius: 10,
              maxWidth: 640,
              maxHeight: 640,
            }}
            variant="rectangular"
            width="100%"
            height="calc(100% - 3rem)"
          />
        )}
        <iframe
          src={file.webViewLink.replace(/\/(edit|view)\?usp=drivesdk.*/, '/viewform?embedded=true')}
          title={file.name}
          width="100%"
          height="100%"
          style={{
            border: 'none',
            minHeight: 480,
            width: 'calc(100% + 4rem)',
            padding: '0 2rem',
            margin: '0 -2rem',
            background: 'transparent',
            opacity: 0,
            transition: `opacity .3s .${delay}s`,
            colorScheme: 'none',
          }}
          onLoad={(e) => {
            const { target } = e;
            if (target instanceof HTMLElement) {
              target.style.opacity = '1';
            }
            timerRef.current = setTimeout(() => setShowSkeleton(false), timeout);
          }}
        />
      </div>
    );
  }

  if (isAVideo(file)) {
    return (
      <div className={cx(styles.content, fileType)} style={{ marginTop: '1.1rem' }}>
        <iframe
          src={`https://drive.google.com/file/d/${file.id}/preview`}
          title={file.name}
          width="100%"
          style={{
            border: 'none',
            aspectRatio: '16 / 9',
            background: 'transparent',
          }}
          allow="autoplay"
        />
      </div>
    );
  }

  if (isAPDF(file) && url) {
    return (
      <div className={cx(styles.content, fileType)} key={file.id}>
        <ErrorBoundary
          FallbackComponent={() => (
            <Paper
              elevation={0}
              className={cx([appStyles.flex, appStyles.alignCenter])}
              style={{
                marginTop: 14,
                border: '1px solid #ffbfbb',
                padding: '1em',
                background: '#FDECEA',
                color: '#611a15',
                lineHeight: 1,
              }}
            >
              <ErrorOutline style={{ marginRight: 12, color: '#f44336' }} />
              Error loading PDF Viewer
            </Paper>
          )}
        >
          <iframe
            style={{ border: 'none', width: '100%', minHeight: 1096, height: '100%', maxWidth: 836 }}
            title={file.name}
            src={`${url}#view=Fit`}
          />
        </ErrorBoundary>
      </div>
    );
  }

  if (!content) return null;

  return (
    <main className={cx(styles.content, fileType)}>
      {indexPage ? (
        <IndexPage content={content} file={file} files={files} wiki={wiki} />
      ) : (
        <Content content={content} />
      )}

      {content.includes('data-react="SkeletonLoader') && (
        <SkeletonMacro containerRef={pageRef} query={`[data-react="SkeletonLoader"]`} />
      )}
      {content && isADocument(file) && content.includes('data-react="Checkbox"') && (
        <CheckboxMacro content={content} query={`[data-react="Checkbox"]`} id={file.id} />
      )}
      {file && content?.includes('data-react="SheetSettings"') && (
        <SheetMacro file={file} query={`[data-react="SheetSettings"]`} />
      )}
      {content.includes('data-react="CopyToClipboard"') && (
        <CopyToClipboardMacro query={`[data-react="CopyToClipboard"]`} />
      )}
    </main>
  );
};

if (LEGACY) {
  let lastHtmlLength = 0;
  let lastId: string | undefined;
  const WCPage = ({
    html: legacyHTML,
    file,
    files,
    wiki,
  }: {
    html: string | undefined;
    file: DriveFile;
    files: DriveFile[];
    wiki: DriveFile;
  }) => {
    // LEGACY <wc-page /> will provide the last legacyHTML in props when a user navigates
    const isStale = file.id !== lastId && lastHtmlLength === legacyHTML?.length;
    useEffect(() => {
      if (isStale) {
        lastHtmlLength = legacyHTML?.length ?? 0;
        lastId = file.id;
      }
      return () => {
        lastHtmlLength = 0;
        lastId = undefined;
      };
    }, [file.id, legacyHTML?.length, isStale]);

    const { data: exportData } = useExport(isStale ? undefined : file, legacyHTML);
    const { html, markdown } = exportData ?? {};
    const { content } = html !== undefined ? parseExport({ html, markdown, file, files, wiki }) : {};
    if (html === undefined && content === undefined) return null;

    const empty = !content || (html && isEmpty(content));
    if (empty) {
      return <EditButton file={file} wiki={wiki} animation="left" />;
    }

    return (
      <>
        <Content content={content} />
        {/* Macros */}
        {content.includes('data-react="SkeletonLoader') && <SkeletonMacro query={`[data-react="SkeletonLoader"]`} />}
        {content && isADocument(file) && content.includes('data-react="Checkbox"') && (
          <CheckboxMacro content={content} query={`[data-react="Checkbox"]`} id={file.id} />
        )}
        {file && content.includes('data-react="SheetSettings"') && (
          <SheetMacro file={file} query={`[data-react="SheetSettings"]`} />
        )}
        {content.includes('data-react="CopyToClipboard"') && (
          <CopyToClipboardMacro query={`[data-react="CopyToClipboard"]`} />
        )}
      </>
    );
  };

  registerComponent(WCPage, 'wc-page', {
    html: 'string',
    file: 'json',
    files: 'json',
    wiki: 'json',
  });
}
